import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getAllDonations, updateDonationStatus, deleteAllDonations, Donation as FirebaseDonation } from '../firebase/donationService';
import { subscribeToRecentDonations, updateTargetAmount } from '../firebase/realtimeDonationService';
import { useDonationData } from '../contexts/DonationContext';

// Use the same interface as the Firebase Donation type but ensure id is always defined
interface Donation extends Omit<FirebaseDonation, 'id'> {
  id: string;
}

// Styled components for the admin dashboard
const DashboardContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 1rem 2.5rem;
  background-color: #f9fafb;
`;

const DashboardContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const DashboardTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
`;

const LogoutButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #f3f4f6;
  color: #1f2937;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const DangerButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 1rem;
  
  &:hover {
    background-color: #dc2626;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ConfirmationModal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ConfirmationBox = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 0.5rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`;

const ConfirmationTitle = styled.h3`
  margin-top: 0;
  color: #ef4444;
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ErrorAlert = styled.div`
  padding: 1rem;
  background-color: #fef2f2;
  color: #b91c1c;
  border-radius: 0.375rem;
  margin-bottom: 1.5rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--primary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeadCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableBody = styled.tbody`
  & tr:not(:last-child) {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
`;

const StatusBadge = styled.span<{ status: 'pending' | 'completed' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.status === 'completed' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
  color: ${props => props.status === 'completed' ? '#047857' : '#b45309'};
`;

const ActionButton = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { totalCollected, targetAmount, donationCount, pendingDonations } = useDonationData();

  const [isAuthenticated, setIsAuthenticated] = useState(true); // Always authenticated
  const [donations, setDonations] = useState<Donation[]>([]);
  const [realtimeDonations, setRealtimeDonations] = useState<Donation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState('Charity does not decrease wealth - Prophet Muhammad (SAW)');
  const [newQuote, setNewQuote] = useState('');
  const [newTargetAmount, setNewTargetAmount] = useState('');
  const [isUpdatingTarget, setIsUpdatingTarget] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Using the password from the header component (627K000BIDI)
  // No hardcoded admin secret key needed here as authentication happens in the Header component

  useEffect(() => {
    // Always assume user is authenticated since they came through the hidden click mechanism
    setIsAuthenticated(true);
    fetchDonations();

    // Set up real-time listener for Recent Offers
    const unsubscribe = subscribeToRecentDonations((realtimeDons) => {
      // Filter out any donations without an ID and cast to the local Donation type
      const typedDonations = realtimeDons
        .filter(donation => donation.id !== undefined)
        .map(donation => ({
          ...donation,
          id: donation.id as string // Assert ID is defined
        }));
      setRealtimeDonations(typedDonations);
    }, 20); // Get more donations for admin view

    return () => {
      unsubscribe();
    };
  }, []);

  // No need for handleLogin as authentication happens in the Header component

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    navigate('/');
  };

  const fetchDonations = async () => {
    try {
      setIsLoading(true);
      const fetchedDonations = await getAllDonations();
      // Ensure all items have a defined id (TypeScript type safety)
      const typedDonations: Donation[] = fetchedDonations.filter(donation => donation.id !== undefined) as Donation[];
      setDonations(typedDonations);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching donations:', err);
      setError('Failed to load donations');
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (donationId: string, newStatus: 'pending' | 'completed') => {
    try {
      await updateDonationStatus(donationId, newStatus);
      // Update local state
      setDonations(donations.map(donation =>
        donation.id === donationId
          ? { ...donation, status: newStatus }
          : donation
      ));
    } catch (err) {
      console.error('Error updating donation status:', err);
      setError('Failed to update donation status');
    }
  };

  const handleExportCSV = () => {
    // Create CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "ID,Name,Phone,Amount,Type,Status,Created At\n";

    donations.forEach(donation => {
      csvContent += `${donation.id},${donation.name},${donation.phone},${donation.amount},${donation.isRecurring ? 'Recurring' : 'One-Time'},${donation.status},${donation.createdAt}\n`;
    });

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `donations_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);

    // Trigger download
    link.click();
    document.body.removeChild(link);
  };

  const handleUpdateQuote = () => {
    if (newQuote.trim()) {
      setQuote(newQuote);
      setNewQuote('');
      // In a real implementation, this would update the quote in Firebase
    }
  };
  
  const handleClearAllData = async () => {
    setIsClearing(true);
    try {
      await deleteAllDonations();
      
      // Update local state
      setDonations([]);
      setRealtimeDonations([]);
      setShowConfirmation(false);
      
      // Show success message
      setError('');
      alert('All donation data has been successfully cleared!');
    } catch (err) {
      console.error('Error clearing donation data:', err);
      setError('Failed to clear donation data. Please try again.');
    } finally {
      setIsClearing(false);
    }
  };
  
  const handleUpdateTargetAmount = async () => {
    // Validate input
    const amount = parseFloat(newTargetAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid target amount');
      return;
    }
    
    setIsUpdatingTarget(true);
    setError('');
    
    try {
      await updateTargetAmount(amount);
      setNewTargetAmount('');
      alert(`Target amount updated to ₹${amount.toLocaleString()}`);
    } catch (err) {
      console.error('Error updating target amount:', err);
      setError('Failed to update target amount. Please try again.');
    } finally {
      setIsUpdatingTarget(false);
    }
  };

  // No login page needed as authentication happens in the Header component

  return (
    <DashboardContainer>
      <DashboardContent>
        <HeaderRow>
          <DashboardTitle>Admin Dashboard</DashboardTitle>
          <ButtonsContainer>
            <DangerButton onClick={() => setShowConfirmation(true)}>Clear All Data</DangerButton>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </ButtonsContainer>
        </HeaderRow>
        
        {/* Confirmation Modal */}
        <ConfirmationModal isVisible={showConfirmation}>
          <ConfirmationBox>
            <ConfirmationTitle>Delete All Donation Data?</ConfirmationTitle>
            <p>This action will permanently delete all donation records from both the database and real-time data. This cannot be undone.</p>
            <p>Are you sure you want to proceed?</p>
            <ConfirmationButtons>
              <LogoutButton onClick={() => setShowConfirmation(false)}>Cancel</LogoutButton>
              <DangerButton onClick={handleClearAllData} disabled={isClearing}>
                {isClearing ? 'Deleting...' : 'Delete All Data'}
              </DangerButton>
            </ConfirmationButtons>
          </ConfirmationBox>
        </ConfirmationModal>

        {error && <ErrorAlert>{error}</ErrorAlert>}

        {/* Real-time Stats Overview */}
        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>Donation Overview</CardTitle>
          </CardHeader>
          <CardBody>
            <StatsGrid>
              <StatCard>
                <StatLabel>TOTAL COLLECTED</StatLabel>
                <StatValue>₹{totalCollected.toLocaleString()}</StatValue>
              </StatCard>

              <StatCard>
                <StatLabel>TARGET AMOUNT</StatLabel>
                <StatValue style={{ color: 'var(--accent)' }}>₹{targetAmount.toLocaleString()}</StatValue>
              </StatCard>

              <StatCard>
                <StatLabel>TOTAL DONATIONS</StatLabel>
                <StatValue style={{ color: '#3b82f6' }}>{donationCount}</StatValue>
              </StatCard>

              <StatCard>
                <StatLabel>PENDING DONATIONS</StatLabel>
                <StatValue style={{ color: '#f59e0b' }}>{pendingDonations}</StatValue>
              </StatCard>
            </StatsGrid>

            {/* Progress Bar */}
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>Campaign Progress</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)' }}>
                  {Math.min(Math.round((totalCollected / targetAmount) * 100), 100)}%
                </span>
              </div>
              <div style={{ width: '100%', height: '0.75rem', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(Math.round((totalCollected / targetAmount) * 100), 100)}%`,
                    backgroundColor: 'var(--primary)',
                    borderRadius: '9999px',
                    transition: 'width 0.5s ease-in-out'
                  }}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Target Amount Editor */}
        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>Update Target Amount</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontStyle: 'italic', color: '#6b7280' }}>Current target: ₹{targetAmount.toLocaleString()}</p>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <input
                type="number"
                value={newTargetAmount}
                onChange={(e) => setNewTargetAmount(e.target.value)}
                placeholder="Enter new target amount"
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.875rem'
                }}
              />
              <ActionButton 
                onClick={handleUpdateTargetAmount}
                disabled={isUpdatingTarget}
              >
                {isUpdatingTarget ? 'Updating...' : 'Update Target'}
              </ActionButton>
            </div>
          </CardBody>
        </Card>

        {/* Quote Editor */}
        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>Inspiration Quote Editor</CardTitle>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontStyle: 'italic', color: '#6b7280' }}>"{quote}"</p>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <input
                type="text"
                value={newQuote}
                onChange={(e) => setNewQuote(e.target.value)}
                placeholder="Enter new quote"
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.875rem'
                }}
              />
              <ActionButton onClick={handleUpdateQuote}>Update Quote</ActionButton>
            </div>
          </CardBody>
        </Card>

        {/* Real-time Donations Table */}
        <Card>
          <CardHeader>
            <CardTitle>Real-time Donations</CardTitle>
            <ActionButton onClick={handleExportCSV}>Export CSV</ActionButton>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ display: 'inline-block', width: '2rem', height: '2rem', border: '3px solid rgba(0, 0, 0, 0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading donations...</p>
              </div>
            ) : realtimeDonations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#6b7280' }}>No donations found.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <tr>
                      <TableHeadCell>Name</TableHeadCell>
                      <TableHeadCell>Phone</TableHeadCell>
                      <TableHeadCell>Amount</TableHeadCell>
                      <TableHeadCell>Type</TableHeadCell>
                      <TableHeadCell>Status</TableHeadCell>
                      <TableHeadCell>Date</TableHeadCell>
                      <TableHeadCell>Actions</TableHeadCell>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {realtimeDonations.map((donation) => {
                      // Format date
                      const date = new Date(donation.createdAt);
                      const formattedDate = date.toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });

                      return (
                        <tr key={donation.id}>
                          <TableCell style={{ fontWeight: 500 }}>
                            {donation.name}
                          </TableCell>
                          <TableCell>
                            {donation.phone}
                          </TableCell>
                          <TableCell style={{ color: 'var(--primary)', fontWeight: 600 }}>
                            ₹{donation.amount.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {donation.isRecurring ? 'Monthly' : 'One-Time'}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={donation.status}>
                              {donation.status === 'completed' ? 'Completed' : 'Pending'}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            {formattedDate}
                          </TableCell>
                          <TableCell>
                            {donation.status === 'pending' ? (
                              <ActionButton
                                onClick={() => handleStatusUpdate(donation.id, 'completed')}
                                style={{ backgroundColor: '#047857' }}
                              >
                                Complete
                              </ActionButton>
                            ) : (
                              <ActionButton
                                onClick={() => handleStatusUpdate(donation.id, 'pending')}
                                style={{ backgroundColor: '#b45309' }}
                              >
                                Pending
                              </ActionButton>
                            )}
                          </TableCell>
                        </tr>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </DashboardContent>
    </DashboardContainer>
  );
};

export default AdminDashboard;
